import { SdkContext } from '../internal'
import type { Space } from '@/types/spaces'
import type { FeedSidebarPerson } from './feed'

/** One tag of a list space, with how many readable posts carry it. */
export interface SpaceSidebarTag {
  tag: string
  key: string
  count: number
}

export interface SpaceSidebarTrendingItem {
  id: string
  slug: string
  title: string
  imageUrl: string | null
  publishedAt: string
  reactions: number
  comments: number
  /** Where it came from: engagement in the last 30 days, most reacted of all time, or the newest. */
  basis?: 'recent' | 'allTime' | 'latest'
  author: FeedSidebarPerson | null
}

/** `GET /api/spaces/{id}/sidebar`: only the widgets with items (`{}` for a module without a column). */
export interface SpaceSidebarData {
  tags?: { items: SpaceSidebarTag[]; total: number }
  trending?: { items: SpaceSidebarTrendingItem[] }
}

/** `GET /api/spaces/{id}/retention-preview` (F-203a, SEC-329): what a retention period would anonymize today. */
export interface RetentionPreview {
  spaceId: string
  module: 'attendance' | 'parcels'
  /** The period in force today. */
  currentMonths: number
  months: number
  cutoff: string
  /** When `months` would apply: now, or 7 days after saving when it is shorter. */
  effectiveAt: string
  counts: {
    guestVisits?: number
    visitorInvitations?: number
    parcels?: number
    parcelPhotos?: number
    parcelAuditLogs?: number
  }
}

export class SpacesResource {
  constructor(private ctx: SdkContext) {}

  async list(params: Record<string, any> = {}) {
    return this.ctx.call({ httpMethod: 'GET', path: '/spaces' }, { query: params })
  }

  async get(id: string) {
    return this.ctx.call<Space | null>({ httpMethod: 'GET', path: `/spaces/{id}` }, { id })
  }

  /** The side column of a list space (a Content space: tags and trending); visitors too. */
  async getSidebar(id: string) {
    return this.ctx.call<SpaceSidebarData>({ httpMethod: 'GET', path: `/spaces/{id}/sidebar` }, { id })
  }

  /** Owner and admin of an Attendance or Parcels space: the counts behind the "change the retention period" confirmation. */
  async retentionPreview(id: string, months: number) {
    return this.ctx.call<RetentionPreview>({ httpMethod: 'GET', path: `/spaces/{id}/retention-preview` }, { id, query: { months } })
  }

  async create(spaceData: any) {
    return this.ctx.call({ httpMethod: 'POST', path: '/spaces', hasBody: true }, { body: spaceData })
  }

  async update(id: string, spaceData: any) {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/spaces/{id}`, hasBody: true },
      { id, body: spaceData }
    )
  }

  async delete(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/spaces/{id}` }, { id })
  }

  /** Module catalog grouped by category, for the create-space picker. Owner/admin only. */
  async listModules() {
    return this.ctx.call({ httpMethod: 'GET', path: '/spaces/modules' })
  }

  async getBySection(sectionId: string) {
    return this.list({ sectionId })
  }

  async reorderSpaces(sectionId: string, spaces: Array<{ id: string; order: number }>) {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/spaces/reorder', hasBody: true },
      { body: { sectionId, spaces } }
    )
  }
}
